import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowsLeftRightIcon } from '@phosphor-icons/react';

import type { CardDetail, CardEvent, ProjectId, SquadId } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import {
  boardRepository,
  projectMembersRepository,
  projectsRepository,
} from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext } from '@web/ui/core';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@web/ui/core/atoms';
import { PopoverMenu } from '@web/ui/core/molecules/popover_menu';
import { fireAndForget } from '@web/ui/core/utils';

type Action = 'agent' | 'squad' | 'triage' | 'ai';

// `t` requires literal keys (keySeparator disabled), so map explicitly.
const MENU_ITEMS: ReadonlyArray<{ action: Action; label: Parameters<typeof t>[0] }> = [
  { action: 'agent', label: 'board.drawer.transfer.menu.agent' },
  { action: 'squad', label: 'board.drawer.transfer.menu.squad' },
  { action: 'triage', label: 'board.drawer.transfer.menu.triage' },
  { action: 'ai', label: 'board.drawer.transfer.menu.ai' },
];

/**
 * Card sem squad é card de triagem: ali só "transferir para squad" faz sentido.
 * "Para outro atendente" depende do `projectId` (null na triagem, o Select
 * vem vazio e o confirmar nunca habilita) e "devolver para triagem" seria
 * no-op — o card já está na caixa. "Devolver para a IA" só existe em card com
 * conversa (é ela que volta a ser respondida pela IA) e vale também na
 * triagem: um card devolvido para lá deixa o chat mudo até alguém devolvê-lo.
 * Com ticket do Zendesk o chat é da mesa, que só o devolve pelo próprio
 * encerramento — o conversation ignora a volta, e o card ficaria sem dono com
 * o chat ainda na mesa.
 */
function menuItems(detail: CardDetail): typeof MENU_ITEMS {
  const canReturnToAi =
    detail.externalRefs.some((r) => r.system === 'conversation') &&
    !detail.externalRefs.some((r) => r.system === 'zendesk' && r.type === 'ticket');
  return MENU_ITEMS.filter((item) => {
    if (item.action === 'ai') return canReturnToAi;
    return detail.squadId !== null || item.action === 'squad';
  });
}

/**
 * Transfer affordance for the card header: an anchored menu (PopoverMenu, same
 * primitive as the profile menu) with three actions, each opening its own
 * focused dialog. Rendered only for transferable (open) cards.
 */
export function TransferMenu({
  detail,
  onTransferred,
}: Readonly<{ detail: CardDetail; onTransferred: () => void }>): React.JSX.Element {
  const [active, setActive] = useState<Action | null>(null);
  return (
    <>
      <PopoverMenu
        side="bottom"
        align="end"
        width="md"
        renderTrigger={({ onClick, open }) => (
          <Button
            variant="outline"
            size="sm"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={onClick}
          >
            <ArrowsLeftRightIcon size={14} weight="bold" aria-hidden />
            {t('board.drawer.action.transfer')}
          </Button>
        )}
      >
        {({ close }) => (
          <div className="flex flex-col">
            {menuItems(detail).map((item) => (
              <button
                key={item.action}
                type="button"
                onClick={() => {
                  setActive(item.action);
                  close();
                }}
                className="flex w-full cursor-pointer items-center rounded-sm border-0 bg-transparent px-2 py-1.5 text-left text-meta text-foreground transition-colors hover:bg-muted"
              >
                {t(item.label)}
              </button>
            ))}
          </div>
        )}
      </PopoverMenu>

      {active === 'agent' ? (
        <AgentTransferDialog
          detail={detail}
          onClose={() => {
            setActive(null);
          }}
          onTransferred={onTransferred}
        />
      ) : null}
      {active === 'squad' ? (
        <SquadTransferDialog
          detail={detail}
          onClose={() => {
            setActive(null);
          }}
          onTransferred={onTransferred}
        />
      ) : null}
      {active === 'ai' ? (
        <AiConfirmDialog
          detail={detail}
          onClose={() => {
            setActive(null);
          }}
          onTransferred={onTransferred}
        />
      ) : null}
      {active === 'triage' ? (
        <TriageConfirmDialog
          detail={detail}
          onClose={() => {
            setActive(null);
          }}
          onTransferred={onTransferred}
        />
      ) : null}
    </>
  );
}

function useTransfer(detail: CardDetail, onDone: () => void) {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (event: CardEvent['event']): Promise<unknown> =>
      boardRepository.dispatchCardEvent(orgId, detail.id, { event }),
    onSuccess: () => {
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(orgId, detail.id) }),
      );
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.boards.all }));
      // Qualquer transferência entra ou sai da caixa da triagem: a `TriagePage`
      // fica montada atrás do card e, sem isso, lista e badge ficam stale
      // (staleTime 30s, sem refetch no foco) — o auto-redirect devolveria o
      // operador justamente para o card recém-encaminhado.
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.triage.inbox(orgId) }));
      // `overview.all` cobre os contadores da sidebar e o snapshot da home
      // ("Você está atendendo"), que também muda com a transferência.
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.overview.all }));
      // The transfer moved/reassigned the card — close the whole drawer
      // (which also unmounts this dialog), returning to the board.
      onDone();
    },
  });
}

function TransferActionDialog({
  title,
  onClose,
  canConfirm,
  pending,
  onConfirm,
  confirmLabel,
  children,
}: Readonly<{
  title: string;
  onClose: () => void;
  canConfirm: boolean;
  pending: boolean;
  onConfirm: () => void;
  confirmLabel: string;
  children: ReactNode;
}>): React.JSX.Element {
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent>
        <DialogTitle className="text-base font-medium text-foreground">{title}</DialogTitle>
        {children}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('board.drawer.transfer.cancel')}
          </Button>
          <Button variant="primary" size="sm" disabled={!canConfirm || pending} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AgentTransferDialog({
  detail,
  onClose,
  onTransferred,
}: Readonly<{
  detail: CardDetail;
  onClose: () => void;
  onTransferred: () => void;
}>): React.JSX.Element {
  const { orgId } = useOrgContext();
  const [userId, setUserId] = useState<string | undefined>();
  const projectId = detail.projectId;
  const members = useQuery({
    queryKey: projectId ? queryKeys.projects.members(orgId, projectId) : ['noop'],
    queryFn: () => projectMembersRepository.list(orgId, projectId as ProjectId),
    enabled: projectId !== null,
  });
  const transfer = useTransfer(detail, onTransferred);
  return (
    <TransferActionDialog
      title={t('board.drawer.transfer.agent.title')}
      onClose={onClose}
      canConfirm={!!userId}
      pending={transfer.isPending}
      onConfirm={() => {
        if (userId) transfer.mutate({ type: 'card.transfer-agent', userId });
      }}
      confirmLabel={t('board.drawer.transfer.submit')}
    >
      <Select value={userId} onValueChange={setUserId}>
        <SelectTrigger>
          <SelectValue placeholder={t('board.drawer.transfer.agent.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {(members.data ?? []).map((m) => (
            <SelectItem key={m.userId} value={m.userId}>
              {m.userName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TransferActionDialog>
  );
}

function SquadTransferDialog({
  detail,
  onClose,
  onTransferred,
}: Readonly<{
  detail: CardDetail;
  onClose: () => void;
  onTransferred: () => void;
}>): React.JSX.Element {
  const { orgId } = useOrgContext();
  const [squadSel, setSquadSel] = useState<
    { squadId: SquadId; projectId: ProjectId } | undefined
  >();
  const projects = useQuery({
    queryKey: queryKeys.projects.list(orgId),
    queryFn: () => projectsRepository.loadProjects(orgId),
  });
  const transfer = useTransfer(detail, onTransferred);
  return (
    <TransferActionDialog
      title={t('board.drawer.transfer.squad.title')}
      onClose={onClose}
      canConfirm={!!squadSel}
      pending={transfer.isPending}
      onConfirm={() => {
        if (squadSel)
          transfer.mutate({
            type: 'card.transfer-squad',
            squadId: squadSel.squadId,
            projectId: squadSel.projectId,
          });
      }}
      confirmLabel={t('board.drawer.transfer.submit')}
    >
      <Select
        value={squadSel?.squadId}
        onValueChange={(v) => {
          const sq = v as SquadId;
          const proj = projects.data?.find((p) => p.squads.some((s) => s.id === sq));
          if (proj) setSquadSel({ squadId: sq, projectId: proj.id });
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder={t('board.drawer.transfer.squad.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {(projects.data ?? [])
            .flatMap((p) => p.squads)
            .filter((s) => s.id !== detail.squadId)
            .map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </TransferActionDialog>
  );
}

function TriageConfirmDialog({
  detail,
  onClose,
  onTransferred,
}: Readonly<{
  detail: CardDetail;
  onClose: () => void;
  onTransferred: () => void;
}>): React.JSX.Element {
  const transfer = useTransfer(detail, onTransferred);
  return (
    <TransferActionDialog
      title={t('board.drawer.transfer.triage.title')}
      onClose={onClose}
      canConfirm
      pending={transfer.isPending}
      onConfirm={() => {
        transfer.mutate({ type: 'card.return-to-triage' });
      }}
      confirmLabel={t('board.drawer.transfer.triage.confirm')}
    >
      <p className="text-meta text-muted-foreground">{t('board.drawer.transfer.triage.hint')}</p>
    </TransferActionDialog>
  );
}

function AiConfirmDialog({
  detail,
  onClose,
  onTransferred,
}: Readonly<{
  detail: CardDetail;
  onClose: () => void;
  onTransferred: () => void;
}>): React.JSX.Element {
  const transfer = useTransfer(detail, onTransferred);
  return (
    <TransferActionDialog
      title={t('board.drawer.transfer.ai.title')}
      onClose={onClose}
      canConfirm
      pending={transfer.isPending}
      onConfirm={() => {
        transfer.mutate({ type: 'card.return-to-agent' });
      }}
      confirmLabel={t('board.drawer.transfer.ai.confirm')}
    >
      <p className="text-meta text-muted-foreground">{t('board.drawer.transfer.ai.hint')}</p>
    </TransferActionDialog>
  );
}
